import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, KeyRound, Loader2, Mail, MailCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { chamarApi } from '@/nucleo/auth/cliente-api';
import { MENSAGEM_GENERICA } from '@/nucleo/auth/mensagens';
import { Alert, AlertDescription, AlertTitle } from '@/ui/base/alert';
import { Button } from '@/ui/base/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/ui/base/form';
import { Input } from '@/ui/base/input';

import { LayoutAutenticacao } from './LayoutAutenticacao';

const esquema = z.object({
  email: z
    .string()
    .min(1, 'Informe o e-mail cadastrado.')
    .email('Confira o e-mail: parece haver um erro de digitação.')
    .max(320, 'E-mail longo demais.'),
});

type Formulario = z.infer<typeof esquema>;

interface EstadoEsqueciSenha {
  titulo: string;
  descricao: string;
  fase: 'PRONTO' | 'CONFIRMADO' | 'LIMITE_ATINGIDO' | 'AGUARDANDO_PROVIDENCIA';
  carregando: boolean;
  campo: {
    rotulo: string;
    descricao: string;
    tipo: 'email';
    tamanhoMaximo: number;
    erro: string | null;
  };
  protecaoAbuso: { mensagem: string };
  confirmacao: { titulo: string; mensagem: string; validadeMinutos: number } | null;
  erro: { titulo: string; mensagem: string; correlacao: string | null } | null;
  orientacaoInstitucional: string | null;
  comandos: Array<{ verbo: 'solicitar_recuperacao'; rotulo: string; habilitado: boolean }>;
  navegacao: Array<{ rotulo: string; caminho: '/login' }>;
}

interface EnvelopeEsqueciSenha {
  contract_version: string;
  screen: 'esqueci-minha-senha';
  event: { verb: string; outcome: 'SUCCESS' | 'REJECTED'; at: string };
  state: EstadoEsqueciSenha;
}

const NAMESPACE = '/bff/v1/esqueci-minha-senha';

export function EsqueciMinhaSenhaPage() {
  const [estado, setEstado] = useState<EstadoEsqueciSenha | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [falhaTransporte, setFalhaTransporte] = useState<string | null>(null);

  const formulario = useForm<Formulario>({
    resolver: zodResolver(esquema),
    defaultValues: { email: '' },
    mode: 'onSubmit',
  });

  useEffect(() => {
    chamarApi<EnvelopeEsqueciSenha>(`${NAMESPACE}/state`, { publica: true })
      .then((envelope) => setEstado(envelope.state))
      .catch(() => setFalhaTransporte(MENSAGEM_GENERICA))
      .finally(() => setCarregando(false));
  }, []);

  async function enviar(dados: Formulario) {
    setCarregando(true);
    setFalhaTransporte(null);
    try {
      const envelope = await chamarApi<EnvelopeEsqueciSenha>(
        `${NAMESPACE}/verbs/solicitar_recuperacao`,
        { metodo: 'POST', corpo: { email: dados.email.trim() }, publica: true },
      );
      setEstado(envelope.state);
    } catch {
      setFalhaTransporte(MENSAGEM_GENERICA);
    } finally {
      setCarregando(false);
    }
  }

  const confirmado = estado?.fase === 'CONFIRMADO' && estado.confirmacao;
  const comando = estado?.comandos.find((item) => item.verbo === 'solicitar_recuperacao');

  return (
    <LayoutAutenticacao
      titulo={estado?.titulo ?? 'Esqueci minha senha'}
      descricao={estado?.descricao ?? 'Consultando o canal seguro de recuperação…'}
    >
      <main aria-busy={carregando} className="space-y-5">
        {carregando && !estado && (
          <div role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Carregando recuperação segura…
          </div>
        )}

        {confirmado && (
          <>
            <Alert className="border-success/40 bg-success/10">
              <MailCheck className="h-4 w-4 text-success" aria-hidden="true" />
              <AlertTitle>{confirmado.titulo}</AlertTitle>
              <AlertDescription>{confirmado.mensagem}</AlertDescription>
            </Alert>
            <div className="rounded-xl border border-border bg-muted/40 p-4">
              <p className="text-xs leading-relaxed text-muted-foreground">
                O link vale{' '}
                <strong className="font-semibold text-foreground">
                  {confirmado.validadeMinutos} minutos
                </strong>{' '}
                e serve uma única vez.
              </p>
            </div>
          </>
        )}

        {(falhaTransporte || estado?.erro) && (
          <Alert variant="destructive">
            <KeyRound className="h-4 w-4" aria-hidden="true" />
            <AlertTitle>{estado?.erro?.titulo ?? 'Não foi possível registrar o pedido'}</AlertTitle>
            <AlertDescription>{estado?.erro?.mensagem ?? falhaTransporte}</AlertDescription>
          </Alert>
        )}

        {estado?.fase === 'PRONTO' && comando?.habilitado && (
          <Form {...formulario}>
            <form onSubmit={formulario.handleSubmit(enviar)} className="space-y-5" noValidate>
              <FormField
                control={formulario.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{estado.campo.rotulo}</FormLabel>
                    <div className="relative">
                      <Mail
                        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                        aria-hidden="true"
                      />
                      <FormControl>
                        <Input
                          {...field}
                          type={estado.campo.tipo}
                          maxLength={estado.campo.tamanhoMaximo}
                          autoComplete="email"
                          inputMode="email"
                          className="h-12 rounded-xl pl-10"
                        />
                      </FormControl>
                    </div>
                    <FormDescription>{estado.campo.descricao}</FormDescription>
                    <FormMessage>{estado.campo.erro}</FormMessage>
                  </FormItem>
                )}
              />
              <p className="text-xs text-muted-foreground">{estado.protecaoAbuso.mensagem}</p>
              <Button
                type="submit"
                size="lg"
                className="h-12 w-full gap-2 text-sm font-semibold"
                disabled={carregando}
              >
                {carregando ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    Enviando…
                  </>
                ) : (
                  comando.rotulo
                )}
              </Button>
            </form>
          </Form>
        )}

        {estado?.orientacaoInstitucional && (
          <p className="text-sm text-muted-foreground">{estado.orientacaoInstitucional}</p>
        )}

        {(
          estado?.navegacao ?? [{ rotulo: 'Voltar para a entrada', caminho: '/login' as const }]
        ).map((item) => (
          <div key={item.caminho} className="border-t border-border pt-4 text-center text-sm">
            <Link
              to={item.caminho}
              className="inline-flex items-center gap-1.5 text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
              {item.rotulo}
            </Link>
          </div>
        ))}
      </main>
    </LayoutAutenticacao>
  );
}

export default EsqueciMinhaSenhaPage;
